import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { messageHandler } from "utils/messageHandler";
import { AGENT_MESSAGES } from "utils/messages";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import {
  addressLine1Validation,
  cityValidation,
  emailValidation,
  firstNameValidation,
  lastNameValidation,
  passwordValidation,
  phoneValidation,
  stateValidation,
  statusValidation,
  zipcodeValidation,
} from "validationSchemas/common";
import AgentForm, { AgentFormValues, SelectOption } from "components/AgentsAddEditComponent";
import { getCloudFileById } from "components/Imageshelper";
import ExistingActiveIdentityConfirmModal, {
  existingActiveIdentityMessage,
  isExistingActiveIdentityConfirmation,
  withExistingActiveIdentityConfirmation,
  type ExistingActiveIdentityError,
} from "components/ExistingActiveIdentityConfirmModal";
import { getAuthCookie } from "../../utils/authCookies";

type BrokerageFirm = {
  id: string;
  firmDetails: { companyName: string };
};

// Select2 type for brokerage firms
type BrokerageOption = {
  value: string;
  label: string;
  id: string;
  firmDetails: {
    companyName: string;
    companyID?: string;
    companyWebsite?: string;
  };
};

type Office = {
  _id: string;
  officeName: string;
  officeType: string;
  officeAddress: Array<{
    _id: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  }>;
};

type AgentAPI = {
  id: string;
  membership_id?: string;
  _id?: string;
  email: string;
  firstName: string;
  lastName: string;
  phone1: string;
  phone2?: string;
  status: string;
  jobTitle?: string;
  profilePic?: string | null;
  brokerageFirmID?: string;
  created_by?: { id?: string; name?: string };
  address?: Partial<{
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    country: string;
    zip: string;
    cityCode: string;
    stateCode: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    phone: string;
  }>;
  professional_details?: Partial<{
    agentID: string;
    licenseNumber: string;
    licenseExpiryDate: string;
  }>;
  firmDetails?: {
    address?: Partial<{
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      country: string;
      zip: string;
      cityCode: string;
      stateCode: string;
      countryCode: string;
      latitude: number;
      longitude: number;
    }>;
  };
  agentDetails?: Partial<{
    brokerageFirmAffiliation: string;
    brokerageFirmID: string;
    agentID: string;
    licenseNumber: string;
    licenseExpiryDate: string;
  }>;
  officeIDs?: string[];
  offices?: Array<{
    _id: string;
    id?: string;
    officeName: string;
    officeType: string;
  }>;
  organization?: {
    _id?: string;
    id?: string;
    name?: string;
    type?: string;
    license_type?: string;
  };
  organization_id?: string;
  userType?: string;
  local_user_type?: string;
  licenseType?: string;
  local_license_type?: string;
  addresses?: {
    personal?: {
      address_details?: Partial<{
        addressLine1: string;
        addressLine2: string;
        city: string;
        state: string;
        country: string;
        zip: string;
        cityCode: string;
        stateCode: string;
        countryCode: string;
        latitude: number;
        longitude: number;
        phone: string;
      }>;
    };
  };
};

const normalizeAgentStatus = (status?: string) => {
  const upper = (status || "").toUpperCase();
  if (upper === "ACTIVE") return "Active";
  if (upper === "INACTIVE") return "Inactive";
  return status || "Active";
};

const isBrokerageAgentMembership = (agent: AgentAPI | null) => {
  if (!agent) return false;
  const licenseType = (agent.licenseType || agent.local_license_type || "").toUpperCase();
  const userType = (agent.userType || agent.local_user_type || "").toLowerCase();
  return licenseType === "BROKERAGE_AGENT" || userType === "brokerage_agent";
};

const isBrokerageOrganization = (org?: AgentAPI["organization"]) => {
  if (!org) return false;
  const orgType = (org.type || "").toLowerCase();
  const orgLicense = (org.license_type || "").toUpperCase();
  return orgType === "brokerage" || orgLicense === "BROKERAGE_FIRM";
};

const getAgentBrokerageFirmId = (agent: AgentAPI | null) => {
  if (!isBrokerageAgentMembership(agent)) return "";
  const org = agent?.organization;
  if (isBrokerageOrganization(org)) {
    return String(org?._id || org?.id || agent?.organization_id || "").trim();
  }
  return String(
    agent?.brokerageFirmID ||
      agent?.agentDetails?.brokerageFirmID ||
      ""
  ).trim();
};

const getAgentBrokerageFirmName = (agent: AgentAPI | null) => {
  if (!isBrokerageAgentMembership(agent)) return "";
  const org = agent?.organization;
  if (isBrokerageOrganization(org)) {
    return org?.name || agent?.agentDetails?.brokerageFirmAffiliation || "";
  }
  return agent?.agentDetails?.brokerageFirmAffiliation || "";
};

const getAgentPersonalAddress = (agent: AgentAPI | null) =>
  agent?.addresses?.personal?.address_details ||
  agent?.address ||
  agent?.firmDetails?.address;

const getAgentOfficeId = (agent: AgentAPI | null) => {
  if (Array.isArray(agent?.officeIDs) && agent.officeIDs.length > 0) {
    return String(agent.officeIDs[0] || "").trim();
  }
  return String(agent?.offices?.[0]?._id || agent?.offices?.[0]?.id || "").trim();
};

const STATUS_OPTIONS: SelectOption[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];
const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

const toUrlString = (res: unknown): string | undefined => {
  if (!res) return undefined;
  if (typeof res === "string") return res;
  const r = res as any;
  const u = r?.url ?? r?.signedUrl ?? r?.data?.url;
  return typeof u === "string" ? u : undefined;
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.readAsDataURL(file);
  });


export default function SignageAddEditAgent() {
  const { agentId } = useParams<{ agentId: string }>();
  const isEdit = Boolean(agentId);
  const navigate = useNavigate();
  const location = useLocation();
  const token = getAuthCookie("sessionToken") || "";
  const membershipId = getAuthCookie("membershipId") || "";
  const organizationId = getAuthCookie("organizationId") || "";
  const sessionLicenseType = String(getAuthCookie("licenseType") || "").trim().toUpperCase();
  const allowExistingMemberAccessConfirm =
    sessionLicenseType === "SIGNAGE_FIRM" || sessionLicenseType === "SIGNAGE_TEAM_MEMBER";



  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  // data sources (always arrays)
  const [offices, setOffices] = useState<Office[]>([]);
  const [agent, setAgent] = useState<AgentAPI | null>(null);
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [serverImageSrc, setServerImageSrc] = useState<string | undefined>(undefined);
  const [cloudFileId, setCloudFileId] = useState<string | null>(null);
  const [identityConfirmOpen, setIdentityConfirmOpen] = useState(false);
  const [identityConfirmMessage, setIdentityConfirmMessage] = useState("");
  const [identityConfirming, setIdentityConfirming] = useState(false);
  const [pendingSave, setPendingSave] = useState<{
    payload: Record<string, unknown>;
    url: string;
    method: string;
    errorData: ExistingActiveIdentityError;
  } | null>(null);
  const [memberAccessPrompt, setMemberAccessPrompt] = useState<{
    message: string;
    name: string;
    email: string;
  } | null>(null);
  const [memberAccessConfirmed, setMemberAccessConfirmed] = useState(false);
  const confirmMemberAccessRef = useRef(false);

  // State for AsyncPaginate selected options
  const [selectedBrokerageOption, setSelectedBrokerageOption] = useState<BrokerageOption | null>(null);

  // Helper function and constants for AsyncPaginate
  const normalizeId = (row: any) =>
    String(
      row?.organization_id ??
        row?.value ??
        row?.membership_id ??
        row?.id ??
        row?._id ??
        ""
    ).trim();
  const PAGE_SIZE = 10;

  // ===== Brokerage Firm Loader =====
  const loadBrokerageOptions: LoadOptions<BrokerageOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      if (!organizationId) {
        return { options: [], hasMore: false, additional: { page } };
      }

      try {
        const q = (inputValue || "").trim();

        const params = new URLSearchParams({
          licenseType: "BROKERAGE_FIRM",
          query: q,
          page: String(page),
          limit: String(PAGE_SIZE),
        });


        //       curl --request GET "http://localhost:5002/api/v1/organizations/6a2713f06cab00ce8198dcd0/memberships/select?licenseType=BROKERAGE_AGENT&page=1&limit=15" `
        // --header "Authorization: Bearer <token>"

        const url = `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/memberships/select?${params}`;

        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData?.error || errorData?.message || "Failed to fetch brokerage firms";
          console.error("Failed to fetch brokerage firms:", errorMessage);
          messageHandler.error("agent", "fetchSingle", new Error(AGENT_MESSAGES.fetchBrokerageFirms.error));
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();
        const list = Array.isArray(json?.data) ? json.data : [];

        const options: BrokerageOption[] = list
          .map((u: any) => {
            const id = normalizeId(u);
            const companyName =
              u?.company_name ||
              u?.firmDetails?.companyName ||
              u?.label ||
              u?.text ||
              "(no name)";
            return {
              value: id,
              label: companyName,
              id,
              firmDetails: {
                companyName,
                companyID: u?.firmDetails?.companyID || id,
                companyWebsite: u?.firmDetails?.companyWebsite || "",
              },
            };
          })
          .filter((option: BrokerageOption) => option.value && option.label);

        const currentPage = Number(json?.page ?? page);
        const totalPages = Number(json?.totalPages ?? currentPage);
        const hasMore = currentPage < totalPages && list.length >= PAGE_SIZE;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        console.error("Failed to load brokerage options:", err);
        messageHandler.error("agent", "fetchSingle", new Error(AGENT_MESSAGES.fetchBrokerageFirms.error));
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [token, organizationId]
  );

  const normalizePath = (p: string) => p.replace(/\\/g, "/").replace(/^\/+/, "");
  const buildAbsoluteFromRelative = (p: string) => {
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    return `${base}/${normalizePath(p)}`;
  };
  useEffect(() => {
    let active = true;

    (async () => {
      const val = agent?.profilePic;
      if (!val) {
        if (active) setServerImageSrc(undefined);
        return;
      }

      if (/^https?:\/\//i.test(val)) {
        if (active) setServerImageSrc(val);
        return;
      }

      if (/^[0-9a-f]{24}$/i.test(val)) {
        setCloudFileId(val);
        try {
          const res = await getCloudFileById(val, token as string);
          // console.log(res,"tetetetete")
          const u = toUrlString(res);
          if (active) setServerImageSrc(u);
        } catch (err) {
          console.error("Failed to load profile image:", err);
          // Optionally show error for image loading - comment out if you want silent failures for images
          messageHandler.error("agent", "fetchSingle", new Error(AGENT_MESSAGES.fetchImage.error));
          if (active) setServerImageSrc(undefined);
        }
        return;
      }

      if (active) setServerImageSrc(buildAbsoluteFromRelative(val));
    })();

    return () => {
      active = false;
    };
  }, [agent?.profilePic, token]); // ⬅️ removed serverImageSrc from deps

  // Removed fetch firms useEffect - now using AsyncPaginate



  // ---------- fetch agent (edit) ----------
  useEffect(() => {
    if (!isEdit) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/organization-memberships/${agentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = (await res.json()) as AgentAPI;
        if (!res.ok) {
          const errorMessage = (data as any)?.error || (data as any)?.message || AGENT_MESSAGES.fetchSingle.error;
          throw new Error(errorMessage);
        }
        setAgent(data);
      } catch (e: any) {
        messageHandler.error("agent", "fetchSingle", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, agentId, token]);
  const DEFAULT_TEN_ZEROS = "0000000000";
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;
  const toCoordinate = (value: unknown) => {
    const coordinate = Number(value ?? 0);
    return Number.isFinite(coordinate) ? coordinate : 0;
  };

  const saveAgent = async (
    payload: Record<string, unknown>,
    url: string,
    method: string
  ) => {
    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const data = (await res.json().catch(() => ({}))) as ExistingActiveIdentityError & {
      message?: string;
    };
    return { res, data };
  };

  // ---------- initial values ----------
  const initialValues: AgentFormValues = useMemo(
    () => {
      const addr = getAgentPersonalAddress(agent);
      const firmId = getAgentBrokerageFirmId(agent);
      return {
      // personal
      firstName: agent?.firstName || "",
      lastName: agent?.lastName || "",
      phone1: agent?.phone1 || DEFAULT_TEN_ZEROS,
      phone2: agent?.phone2 || "",
      // account
      email: agent?.email || "",
      password: "",
      // professional
      brokerageFirmAffiliation: firmId,
      officeId: getAgentOfficeId(agent),
      agentID: agent?.professional_details?.agentID || agent?.agentDetails?.agentID || "",
      licenseNumber: agent?.professional_details?.licenseNumber || agent?.agentDetails?.licenseNumber || "",
      licenseExpiryDate: agent?.professional_details?.licenseExpiryDate || agent?.agentDetails?.licenseExpiryDate || "",
      statuss: normalizeAgentStatus(agent?.status),
      // address
      addressLine1: addr?.addressLine1 || "",
      addressLine2: addr?.addressLine2 || "",
      city: addr?.city || "",
      state: addr?.state || "",
      country: addr?.country || US_COUNTRY_DEFAULTS.country,
      zipcode: addr?.zip || "",
      cityCode: addr?.cityCode || "",
      stateCode: addr?.stateCode || "",
      countryCode: addr?.countryCode || US_COUNTRY_DEFAULTS.countryCode,
      latitude: toCoordinate(addr?.latitude),
      longitude: toCoordinate(addr?.longitude),
      // profile
      image: null,
      profilePic: agent?.profilePic || null,
      displaySrc: agent?.profilePic || null,
    };
    },
    [agent]
  );

  // ---------- formik (parent only) ----------
  const formik = useFormik<AgentFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      phone1: phoneValidation,
      email: emailValidation,
      // password: isEdit ? Yup.string() : passwordValidation,
      // addressLine1: addressLine1Validation,
      // city: cityValidation,
      // state: stateValidation,
      // zipcode: zipcodeValidation,
      // statuss: statusValidation,
    }),
    onSubmit: async (values) => {
      try {
        setFormSubmitting(true);

        if (!organizationId) {
          throw new Error("Organization ID is missing");
        }
        const agentMembershipId = String(
          agent?.membership_id || agent?._id || agent?.id || agentId || ""
        ).trim();

        if (isEdit && !agentMembershipId) {
          throw new Error("Agent membership ID is missing");
        }

        const profilePicPayload =
          values.image instanceof File ? await fileToBase64(values.image) : undefined;

        const brokerageFirmID = String(
          selectedBrokerageOption?.value || values.brokerageFirmAffiliation || ""
        ).trim();
        const isBrokerageAgent = Boolean(brokerageFirmID);

        const addressCountry = values.country?.trim() || US_COUNTRY_DEFAULTS.country;
        const addressCountryCode = values.countryCode?.trim() || US_COUNTRY_DEFAULTS.countryCode;
        const addressLatitude = toCoordinate(values.latitude);
        const addressLongitude = toCoordinate(values.longitude);

        const addressLine1 = String(values.addressLine1 || "").trim();
        const city = String(values.city || "").trim();
        const state = String(values.state || "").trim();
        const zip = String(values.zipcode || "").trim();
        const shouldIncludeAddress = Boolean(
          addressLine1 && city && state && zip
        );

        const payload: Record<string, unknown> = {
          email: values.email,
          password: isEdit ? values.password || "" : values.password || "",
          firstName: values.firstName,
          lastName: values.lastName,
          phone1: withDefault(values.phone1),
          phone2: values.phone2 || "",
          jobTitle: agent?.jobTitle || "",
          ...(profilePicPayload ? { profilePic: profilePicPayload } : {}),
          status: (values.statuss || "Active").toUpperCase(),
          userType: isBrokerageAgent ? "brokerage_agent" : "agent",
          licenseType: isBrokerageAgent ? "BROKERAGE_AGENT" : "AGENT",
          professional_details: {
            agentID: values.agentID,
            licenseNumber: values.licenseNumber,
            licenseExpiryDate: values.licenseExpiryDate,
          },
          ...(shouldIncludeAddress
            ? {
                address: {
                  addressLine1,
                  addressLine2: values.addressLine2 || "",
                  city,
                  state,
                  country: addressCountry,
                  zip,
                  phone: withDefault(values.phone1),
                  latitude: addressLatitude,
                  longitude: addressLongitude,
                  cityCode: values.cityCode,
                  stateCode: values.stateCode,
                  countryCode: addressCountryCode,
                },
              }
            : {}),
        };

        if (isBrokerageAgent) {
          payload.brokerageFirmID = brokerageFirmID;
          if (values.officeId) {
            payload.officeIDs = [values.officeId];
          }
        }
        if (allowExistingMemberAccessConfirm && confirmMemberAccessRef.current) {
          payload.confirm_existing_brokerage_member_access = true;
        }
        confirmMemberAccessRef.current = false;

        const targetOrganizationId = isBrokerageAgent
          ? brokerageFirmID
          : organizationId;
        const url = isEdit
          ? `${process.env.REACT_APP_API_URL}/organizations/${targetOrganizationId}/memberships/agents/${agentMembershipId}`
          : `${process.env.REACT_APP_API_URL}/organizations/${targetOrganizationId}/memberships/agents`;
        const method = isEdit ? "PATCH" : "POST";

        const { res, data } = await saveAgent(payload, url, method);

        if (
          allowExistingMemberAccessConfirm &&
          data?.code === "BROKERAGE_MEMBER_ACCESS_CONFIRMATION_REQUIRED"
        ) {
          const existing = (data as ExistingActiveIdentityError & {
            existing_member?: { name?: string; email?: string };
          }).existing_member;
          setMemberAccessConfirmed(false);
          setMemberAccessPrompt({
            message:
              data.message ||
              "An agent with this email already exists in the brokerage. Do you want to ask the brokerage to give your vendor access to this agent?",
            name: String(existing?.name || "").trim(),
            email: String(existing?.email || "").trim(),
          });
          return;
        }

        if (isExistingActiveIdentityConfirmation(res.status, data)) {
          setPendingSave({ payload, url, method, errorData: data });
          setIdentityConfirmMessage(existingActiveIdentityMessage(data));
          setIdentityConfirmOpen(true);
          return;
        }

        if (!res.ok || data?.error) {
          throw new Error(data?.error || data?.message || "Failed to save agent");
        }

        messageHandler.success("agent", isEdit ? "update" : "create");
        navigate(`${baseRoute}/agents`);
      } catch (e: any) {
        messageHandler.error("agent", isEdit ? "update" : "create", e);
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  const closeMemberAccessPrompt = () => {
    if (formSubmitting) return;
    confirmMemberAccessRef.current = false;
    setMemberAccessConfirmed(false);
    setMemberAccessPrompt(null);
  };

  const confirmExistingMemberAccess = () => {
    if (!memberAccessConfirmed) {
      messageHandler.error(
        "agent",
        isEdit ? "update" : "create",
        new Error("Select the confirmation to continue.")
      );
      return;
    }
    confirmMemberAccessRef.current = true;
    setMemberAccessPrompt(null);
    formik.handleSubmit();
  };

  const handleIdentityConfirmClose = () => {
    setIdentityConfirmOpen(false);
    setPendingSave(null);
    navigate(-1);
  };

  const handleIdentityConfirmOk = async () => {
    if (!pendingSave) return;
    setIdentityConfirming(true);
    try {
      const confirmedPayload = withExistingActiveIdentityConfirmation(
        pendingSave.payload,
        pendingSave.errorData
      );
      const { res, data } = await saveAgent(
        confirmedPayload,
        pendingSave.url,
        pendingSave.method
      );
      if (!res.ok || data?.error) {
        throw new Error(data?.error || data?.message || "Failed to save agent");
      }
      setIdentityConfirmOpen(false);
      setPendingSave(null);
      messageHandler.success("agent", isEdit ? "update" : "create");
      navigate(`${baseRoute}/agents`);
    } catch (e: any) {
      messageHandler.error("agent", isEdit ? "update" : "create", e);
    } finally {
      setIdentityConfirming(false);
    }
  };

  // fetch offices when firm changes
  useEffect(() => {
    const firmId = String(formik.values.brokerageFirmAffiliation || "").trim();
    if (!firmId) {
      setOffices([]);
      return;
    }

    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/organizations/${firmId}/offices`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (!res.ok || data?.error) throw new Error(data?.error || "Failed to fetch offices");
        setOffices(Array.isArray(data) ? (data as Office[]) : []);
      } catch (e: any) {
        messageHandler.error("agent", "fetch", e);
        setOffices([]);
      }
    })();
  }, [formik.values.brokerageFirmAffiliation, token]);

  // ---------- HYDRATE firm/office on EDIT ----------
  // 1) map agent firm name or created_by.id to firm ID → set in Formik and selectedBrokerageOption
  useEffect(() => {
    if (!isEdit || !agent) return;

    const firmId = getAgentBrokerageFirmId(agent);
    const firmName = getAgentBrokerageFirmName(agent);

    if (firmId) {
      const brokerageOption: BrokerageOption = {
        value: firmId,
        label: firmName || firmId,
        id: firmId,
        firmDetails: {
          companyName: firmName || firmId,
          companyID: firmId,
          companyWebsite: "",
        },
      };
      setSelectedBrokerageOption(brokerageOption);

      if (formik.values.brokerageFirmAffiliation !== firmId) {
        formik.setFieldValue("brokerageFirmAffiliation", firmId, false);
      }
    } else {
      setSelectedBrokerageOption(null);
      formik.setFieldValue("brokerageFirmAffiliation", "", false);
    }
  }, [isEdit, agent]);

  // 2) after offices arrive, set agent's officeId
  useEffect(() => {
    if (!isEdit || !agent || !offices.length) return;
    const agentOfficeId = getAgentOfficeId(agent);
    if (agentOfficeId && formik.values.officeId !== agentOfficeId) {
      formik.setFieldValue("officeId", agentOfficeId, false);
    }
  }, [isEdit, agent, offices]);

  if (loading) return <div className="content">Loading...</div>;

  // options for child (always arrays) - now using AsyncPaginate
  const brokerageFirmOptions: SelectOption[] = []; // Always empty since we're using AsyncPaginate

  const officeOptions: SelectOption[] = (offices ?? []).map((o) => ({
    value: o._id,
    label: o.officeName,
  }));
  const joinUrl = (base?: string, path?: string) => {
    if (!path) return "";
    if (!base) return path;
    if (/^https?:\/\//i.test(path)) return path;
    const b = base.replace(/\/+$/, "");
    const p = path.replace(/^\/+/, "");
    return `${b}/${p}`;
  };

  const displaySrc: string | undefined =
    serverImageSrc ??
    (typeof formik.values.profilePic === "string" && /^\/?uploads\//i.test(formik.values.profilePic)
      ? joinUrl(process.env.REACT_APP_BASE_URL, formik.values.profilePic)
      : undefined);

  return (
    <>
      <AgentForm
        isEdit={isEdit}
        formik={formik}
        brokerageFirmOptions={brokerageFirmOptions}
        officeOptions={officeOptions}
        statusOptions={STATUS_OPTIONS}
        displaySrc={displaySrc}
        onCancel={() => navigate(-1)}
        onSubmit={() => formik.handleSubmit()}
        submitting={formSubmitting}
        enableGoogleAddressDetailsFlow
        // AsyncPaginate props
        loadBrokerageOptions={loadBrokerageOptions}
        selectedBrokerageOption={selectedBrokerageOption}
        onBrokerageChange={setSelectedBrokerageOption}
        memberAccessPrompt={memberAccessPrompt}
        memberAccessConfirmed={memberAccessConfirmed}
        onMemberAccessConfirmedChange={setMemberAccessConfirmed}
        onMemberAccessCancel={closeMemberAccessPrompt}
        onMemberAccessSave={confirmExistingMemberAccess}
      />
      <ExistingActiveIdentityConfirmModal
        open={identityConfirmOpen}
        message={identityConfirmMessage}
        confirming={identityConfirming}
        onClose={handleIdentityConfirmClose}
        onConfirm={handleIdentityConfirmOk}
      />
    </>
  );
}
